import { forwardRef, useImperativeHandle, useState } from "react";
import styled from "styled-components";
import {
  useAdminQuarantinedFilesQuery,
  useAdminFilesByQuarantineStatusQuery,
  QuarantineStatus,
} from "../../generated/graphql";
import AdminQuarantineGrid from "./grids/AdminQuarantineGrid";

// Styled Components
const QuarantineContainer = styled.div`
  margin: 20px 0;
`;

const QuarantineHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const QuarantineTitle = styled.h2`
  font-size: 20px;
  margin: 0;
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const StatusFilter = styled.select`
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #ddd;
  font-size: 14px;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
  font-size: 16px;
`;

const Loading = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
  font-size: 16px;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #dc3545;
  font-size: 16px;
`;

// Component interfaces
export interface QuarantineListRefType {
  refetch: () => void;
}

interface AdminQuarantineListProps {}

const AdminQuarantineList = forwardRef<
  QuarantineListRefType,
  AdminQuarantineListProps
>((_, ref) => {
  const [statusFilter, setStatusFilter] = useState<string>("all");

  // Determine which query to use based on filter
  const useQuarantinedQuery =
    statusFilter === "all" || statusFilter === QuarantineStatus.Quarantined;

  const {
    loading: quarantinedLoading,
    error: quarantinedError,
    data: quarantinedData,
    refetch: refetchQuarantined,
  } = useAdminQuarantinedFilesQuery({
    skip: !useQuarantinedQuery,
    fetchPolicy: "network-only",
  });

  const {
    loading: statusLoading,
    error: statusError,
    data: statusData,
    refetch: refetchStatus,
  } = useAdminFilesByQuarantineStatusQuery({
    variables: { status: statusFilter as QuarantineStatus },
    skip: useQuarantinedQuery || statusFilter === "all",
    fetchPolicy: "network-only",
  });

  // Expose refetch method to parent components
  useImperativeHandle(ref, () => ({
    refetch: () => {
      if (useQuarantinedQuery) {
        refetchQuarantined();
      } else {
        refetchStatus();
      }
    },
  }));

  const loading = quarantinedLoading || statusLoading;
  const error = quarantinedError || statusError;

  let files: any[] = [];
  if (useQuarantinedQuery && quarantinedData) {
    files = quarantinedData.admin.quarantinedFiles;
  } else if (statusData) {
    files = statusData.admin.filesByQuarantineStatus;
  }

  if (loading) return <Loading>Loading quarantine data...</Loading>;
  if (error)
    return (
      <ErrorMessage>
        Error loading quarantine data: {error.message}
      </ErrorMessage>
    );

  return (
    <QuarantineContainer>
      <QuarantineHeader>
        <QuarantineTitle>Quarantine Management</QuarantineTitle>
        <FilterContainer>
          <label htmlFor="status-filter">Status:</label>
          <StatusFilter
            id="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Quarantined</option>
            <option value={QuarantineStatus.Quarantined}>Quarantined</option>
            <option value={QuarantineStatus.Clean}>Clean</option>
            <option value={QuarantineStatus.ScanFailed}>Scan Failed</option>
          </StatusFilter>
        </FilterContainer>
      </QuarantineHeader>

      {files.length === 0 ? (
        <EmptyMessage>
          {statusFilter === "all"
            ? "No files in quarantine."
            : `No files with ${statusFilter} status.`}
        </EmptyMessage>
      ) : (
        <AdminQuarantineGrid rows={files} />
      )}
    </QuarantineContainer>
  );
});

AdminQuarantineList.displayName = "AdminQuarantineList";

export default AdminQuarantineList;
